import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError } from 'rxjs';

/**
 * Set to `true` in a request's `HttpContext` to skip the automatic error snackbar.
 *
 * ```ts
 * // HttpClient
 * this.http.get('/api/items', { context: new HttpContext().set(SKIP_API_ERROR_TOAST, true) });
 *
 * // Auto-generated service
 * this.itemsService.itemsGet({ context: new HttpContext().set(SKIP_API_ERROR_TOAST, true) });
 * ```
 */
export const SKIP_API_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * Shows an error snackbar on every failed HTTP request.
 * The message is read from `CommonErrorDto.description` in the response body
 * (`HttpErrorResponse.error`), falling back to a generic string.
 *
 * To suppress the toast for a specific request, set {@link SKIP_API_ERROR_TOAST} to `true`.
 */
const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const withApiErrorInterceptor: HttpInterceptorFn = (req, next) => {
  if (!MUTATING_METHODS.has(req.method) || req.context.get(SKIP_API_ERROR_TOAST)) {
    return next(req);
  }

  const snackBar = inject(MatSnackBar);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const message = error.error?.description ?? 'Something went wrong. Please try again.';
      snackBar.open(message, 'OK', {
        duration: 4000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        panelClass: ['error'],
      });
      throw error;
    })
  );
};
